import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { lerMetaDaEquipeDoAdministrador, salvarMetaDaEquipe } from '../../administracao';
import type { MetaDaEquipeDoAdministrador } from '../../administracao';
import { ErroDaLista, Esqueleto } from '../equipe/Equipe';
import { diaMesHora } from './comunicacao-comum';

/**
 * META DA EQUIPE COM BRINDE — a parte do administrador (1.0: aba "Metas e brindes", bloco "Meta
 * da equipe"). Uma meta de itens para a equipe inteira no mês (ou na semana) e o brinde que vale
 * para todos se a soma bater. Conta como o cheque (itens confirmados com o HOS).
 *
 * Quem fez quanto aparece SÓ aqui (quem gerencia campanhas); o balconista vê só o total da equipe.
 * Mudar é versão nova com motivo — o histórico fica, a trilha de auditoria registra.
 */
const diaMes = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;
const ROTULO = { mes: 'do mês', semana: 'da semana' } as const;

export function MetaDaEquipeDoAdmin({ sessao }: { sessao: Sessao }) {
  const [d, setD] = useState<MetaDaEquipeDoAdministrador | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(async () => {
    setErro(false);
    const r = await lerMetaDaEquipeDoAdministrador();
    if (r.ok) setD(r.dados);
    else setErro(true);
  }, []);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  const m = d?.meta ?? null;
  return (
    <section className="ac-cartao rg-equipe" aria-label="Meta da equipe">
      <div className="rg-campanhas-cabeca">
        <h2 className="rg-campanhas-titulo">Meta da equipe</h2>
        <span className="rg-camada">brinde para todos · não altera a premiação</span>
      </div>
      <div className="rg-equipe-corpo">
        {erro && (
          <ErroDaLista
            titulo="Não foi possível carregar a meta da equipe"
            texto="As regras e as campanhas continuam valendo."
            aoTentar={() => void carregar()}
          />
        )}
        {!erro && d === null && <Esqueleto linhas={3} />}
        {d && (
          <div className="rg-equipe-grade">
            <div className="rg-equipe-estado">
              {m ? (
                <>
                  <div className="rg-equipe-linha">
                    <span>
                      Meta {ROTULO[m.periodo]} · {diaMes(m.de)} a {diaMes(m.ate)}
                    </span>
                    <b>
                      {m.feitos} de {m.alvo} itens
                    </b>
                  </div>
                  <div
                    className={`rg-equipe-barra${m.batida ? ' rg-equipe-barra-ok' : ''}`}
                    role="progressbar"
                    aria-valuenow={m.pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div style={{ width: `${String(m.pct)}%` }} />
                  </div>
                  <p className="rg-publicar-nota">
                    {m.batida
                      ? `Meta batida: ${String(m.feitos)} de ${String(m.alvo)} itens confirmados pela equipe.`
                      : `Faltam ${String(m.faltam)} ${m.faltam === 1 ? 'item confirmado' : 'itens confirmados'}.`}{' '}
                    {m.brinde ? (
                      <>
                        Brinde: <b>{m.brinde}</b>
                        {m.batida ? ' — liberado.' : '.'}
                      </>
                    ) : (
                      'Sem brinde escrito.'
                    )}
                  </p>
                  {d.porPessoa.length > 0 ? (
                    <table className="ac-tabela rg-equipe-tabela">
                      <caption>Quem fez quanto (só administradores veem)</caption>
                      <thead>
                        <tr>
                          <th>PESSOA</th>
                          <th className="ac-direita">ITENS CONFIRMADOS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.porPessoa.map((p) => (
                          <tr key={p.accountId}>
                            <td>{p.nome}</td>
                            <td className="ac-direita">{p.itens}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="rg-publicar-nota">Nenhum item confirmado no período ainda.</p>
                  )}
                </>
              ) : (
                <p className="rg-publicar-nota">
                  Sem meta da equipe neste período. Defina ao lado: os balconistas passam a ver a
                  barra da equipe e o brinde no Início e em Meu desempenho.
                </p>
              )}
              {d.historico.length > 0 && (
                <details className="rg-equipe-historico">
                  <summary>Histórico ({d.historico.length})</summary>
                  <ul>
                    {d.historico.map((h) => (
                      <li key={h.versao}>
                        <b>v{h.versao}</b> · {diaMesHora(h.em)} · {h.por ?? 'sistema'} ·{' '}
                        {h.itens > 0
                          ? `${String(h.itens)} itens ${ROTULO[h.periodo]}${h.brinde ? ` · ${h.brinde}` : ''}`
                          : `desligou a meta ${ROTULO[h.periodo]}`}{' '}
                        — {h.motivo}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
            <FormularioDaMetaDaEquipe
              key={d.versao}
              d={d}
              sessao={sessao}
              aoMudar={() => void carregar()}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function FormularioDaMetaDaEquipe({
  d,
  sessao,
  aoMudar,
}: {
  d: MetaDaEquipeDoAdministrador;
  sessao: Sessao;
  aoMudar: () => void;
}) {
  const [periodo, setPeriodo] = useState<'mes' | 'semana'>(d.meta?.periodo ?? 'mes');
  const [itens, setItens] = useState(d.meta ? String(d.meta.alvo) : '');
  const [brinde, setBrinde] = useState(d.meta?.brinde ?? '');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);
  const salvar = async () => {
    setEnviando(true);
    setMsg(null);
    const x = await salvarMetaDaEquipe(sessao, {
      periodo,
      itens: Number(itens),
      brinde: brinde.trim() || null,
      motivo,
      versaoAtual: d.versao,
    });
    setEnviando(false);
    if (x.tipo === 'confirmada') aoMudar();
    else
      setMsg({
        ok: false,
        texto: x.tipo === 'recusada' ? x.mensagem : 'Não deu para confirmar. Recarregue e confira.',
      });
  };
  return (
    <div className="rg-publicar-form">
      <label>
        <span>Período</span>
        <select
          className="rg-equipe-select"
          value={periodo}
          onChange={(e) => setPeriodo(e.target.value === 'semana' ? 'semana' : 'mes')}
        >
          <option value="mes">Mês atual</option>
          <option value="semana">Semana atual (sábado a sexta)</option>
        </select>
      </label>
      <label>
        <span>Meta (itens da equipe inteira; 0 desliga)</span>
        <input
          inputMode="numeric"
          value={itens}
          placeholder="Ex.: 200"
          onChange={(e) => setItens(e.target.value.replace(/\D/g, '').slice(0, 6))}
        />
      </label>
      <label>
        <span>Brinde</span>
        <input
          value={brinde}
          maxLength={120}
          placeholder="Ex.: Pizza para todo mundo na sexta"
          onChange={(e) => setBrinde(e.target.value)}
        />
      </label>
      <label>
        <span>Motivo (fica no histórico)</span>
        <input value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} />
      </label>
      <p className="rg-publicar-nota">
        Vale na hora, para o período atual. Conta os itens confirmados com o HOS de todos (a mesma
        conta da meta de cada um). Se a soma bater, o brinde vale para todos.
      </p>
      <button
        type="button"
        className="ac-botao-cheio rg-publicar-botao"
        disabled={enviando || itens === '' || motivo.trim().length < 5}
        onClick={() => void salvar()}
      >
        {enviando ? 'Salvando…' : 'Salvar meta da equipe'}
      </button>
      {msg && (
        <p className={msg.ok ? 'rg-publicar-ok' : 'rg-publicar-erro'} role="status">
          {msg.texto}
        </p>
      )}
    </div>
  );
}
